import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertCircle, ShieldAlert, Shield, ShieldCheck, Megaphone, Unlock, MessageSquare, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SteamLoginButton from "@/components/SteamLoginButton";
import AttachmentUploader from "@/components/AttachmentUploader";

const ICONS = { AlertCircle, ShieldAlert, Shield, ShieldCheck, Megaphone, Unlock, MessageSquare };

export default function NewTicketPage() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [selected, setSelected] = useState(null);
  const [values, setValues] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { data: types = [] } = useQuery({
    queryKey: ["ticket-types"],
    queryFn: () => api.get("/tickets/types").then((r) => r.data),
  });

  if (loading) return <div className="py-24 text-center text-muted-foreground">Chargement...</div>;
  if (!user) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center">
        <p className="mb-3">Connectez-vous avec Steam pour ouvrir un ticket.</p>
        <SteamLoginButton />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/tickets", {
        type: selected.key,
        fields: values,
        attachments: attachments.map((a) => a.url),
      });
      toast.success("Ticket ouvert. Le staff a été notifié.");
      nav(`/tickets/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  if (!selected) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="mb-4"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button>
        <h1 className="font-display text-3xl uppercase tracking-tight mb-2">Nouveau ticket</h1>
        <p className="text-muted-foreground mb-8">Choisissez le type de demande. Chaque ticket est confidentiel : seul vous et le staff pouvez le voir.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {types.map((t) => {
            const Ico = ICONS[t.icon] || MessageSquare;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { setSelected(t); setValues({}); }}
                className="text-left"
                data-testid={`ticket-type-${t.key}`}
              >
                <Card className="h-full hover:border-primary/60 transition-colors group">
                  <CardContent className="p-5 flex gap-4">
                    <div className="h-10 w-10 shrink-0 rounded-md bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Ico className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-display uppercase text-lg">{t.label}</p>
                      <p className="text-sm text-muted-foreground mt-1">{t.description}</p>
                    </div>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const Ico = ICONS[selected.icon] || MessageSquare;
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="mb-4"><ArrowLeft className="h-4 w-4 mr-1" /> Changer de type</Button>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center"><Ico className="h-5 w-5" /></div>
        <div>
          <p className="font-mono-tech text-xs uppercase text-muted-foreground">{selected.key}</p>
          <h1 className="font-display text-2xl uppercase tracking-tight">{selected.label}</h1>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {selected.fields.map((f) => (
          <div key={f.name}>
            <label className="text-sm font-medium block mb-1">
              {f.label} {f.required && <span className="text-primary">*</span>}
            </label>
            {f.type === "textarea" ? (
              <Textarea
                value={values[f.name] || ""}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                rows={5}
                maxLength={f.max}
                required={f.required}
                data-testid={`ticket-field-${f.name}`}
              />
            ) : (
              <Input
                type={f.type === "number" ? "number" : f.type === "url" ? "url" : "text"}
                value={values[f.name] || ""}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                maxLength={f.max}
                required={f.required}
                data-testid={`ticket-field-${f.name}`}
              />
            )}
          </div>
        ))}
        <div>
          <label className="text-sm font-medium block mb-1">Pièces jointes (optionnel)</label>
          <AttachmentUploader value={attachments} onChange={setAttachments} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={() => setSelected(null)}>Annuler</Button>
          <Button type="submit" disabled={submitting} data-testid="submit-ticket-button">
            {submitting ? "Envoi..." : "Ouvrir le ticket"}
          </Button>
        </div>
      </form>
    </div>
  );
}
